import { WebDashboardContentSpecSlicersInner } from "@coeff/agent-types";

import { DateRangeSelection } from "./dateRangeSelection";
import { SlicerState } from "./useDashboardRuntime";

// ===========================================================================
// Sticky filters: the viewer's last filter choices, persisted per dashboard in
// localStorage so the dashboard reopens on their last view.
//
// Only DIFFS from the dashboard's saved defaults are stored. An untouched
// filter must keep following the owner's default — persisting it would shadow
// any default the owner saves later with a stale copy of the old one.
//
// Date filters store their structured selection ("Last 30 days"), never the
// dates it resolved to, so a relative choice re-resolves on every visit
// instead of freezing on the day it was made (same rule as slicerDefaults).
//
// Everything here is best-effort: localStorage can be absent, full, or
// poisoned, and none of that may break dashboard load.
// ===========================================================================

export interface StickyFilterSnapshot {
  /** single/multi select values, keyed by slicer name. */
  values: Record<string, string | string[]>;
  /** Structured date-range intents, keyed by slicer name. */
  dateSelections: Record<string, DateRangeSelection>;
}

const STORAGE_KEY_PREFIX = "coeffDashboardStickyFilters:";
const STORAGE_VERSION = 1;

const storageKey = (dashboardId: string) => `${STORAGE_KEY_PREFIX}${dashboardId}`;

/** window.localStorage, or null where touching it throws (sandboxed iframe, private mode). */
export const getStickyStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const isSnapshotEmpty = (snapshot: StickyFilterSnapshot): boolean =>
  Object.keys(snapshot.values).length === 0 && Object.keys(snapshot.dateSelections).length === 0;

/**
 * The current filter state as a sticky snapshot: only entries that differ from the
 * dashboard's defaults.
 *
 * `personalized_view` is never captured — it is an editor's "view as" preview, scoped to an
 * identity rather than the data. A date filter with no structured selection (a data-derived
 * default the viewer never touched) is skipped: its resolved dates are only true today.
 */
export const buildStickySnapshot = (
  slicers: WebDashboardContentSpecSlicersInner[],
  slicerState: SlicerState,
  defaultSlicerState: SlicerState,
  dateRangeSelections: Record<string, DateRangeSelection | null>
): StickyFilterSnapshot => {
  const snapshot: StickyFilterSnapshot = { values: {}, dateSelections: {} };
  for (const slicer of slicers) {
    if (slicer.type === "personalized_view") {
      continue;
    }
    const current = slicerState[slicer.name];
    if (
      current === undefined ||
      JSON.stringify(current) === JSON.stringify(defaultSlicerState[slicer.name])
    ) {
      continue;
    }
    if (slicer.type === "date_range") {
      const selection = dateRangeSelections[slicer.name];
      if (selection) {
        snapshot.dateSelections[slicer.name] = selection;
      }
    } else if (typeof current === "string" || Array.isArray(current)) {
      snapshot.values[slicer.name] = current;
    }
  }
  return snapshot;
};

const SELECTION_KINDS = new Set(["last", "this", "previous", "custom"]);

const isValidSelection = (value: unknown): value is DateRangeSelection =>
  typeof value === "object" &&
  value !== null &&
  SELECTION_KINDS.has((value as { kind?: unknown }).kind as string);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === "string");

/**
 * The stored snapshot for a dashboard, or null when there is none (or it is unreadable).
 *
 * Entries are shape-validated individually — one poisoned value drops that entry, not the
 * whole snapshot. Whether a surviving value still makes sense for the dashboard (the option
 * still exists in the data) is the caller's problem: only it has the resolved options.
 */
export const loadStickyFilters = (
  storage: Storage | null,
  dashboardId: string
): StickyFilterSnapshot | null => {
  let raw: string | null;
  try {
    raw = storage?.getItem(storageKey(dashboardId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as {
      v?: unknown;
      values?: unknown;
      dateSelections?: unknown;
    } | null;
    if (parsed?.v !== STORAGE_VERSION) {
      return null;
    }
    const snapshot: StickyFilterSnapshot = { values: {}, dateSelections: {} };
    if (parsed.values && typeof parsed.values === "object") {
      for (const [name, value] of Object.entries(parsed.values as Record<string, unknown>)) {
        if (typeof value === "string" || isStringArray(value)) {
          snapshot.values[name] = value;
        }
      }
    }
    if (parsed.dateSelections && typeof parsed.dateSelections === "object") {
      for (const [name, selection] of Object.entries(
        parsed.dateSelections as Record<string, unknown>
      )) {
        if (isValidSelection(selection)) {
          snapshot.dateSelections[name] = selection;
        }
      }
    }
    return snapshot;
  } catch {
    return null;
  }
};

/**
 * Persist a snapshot, or forget the dashboard entirely when the snapshot is empty — a state
 * equal to the defaults means there is nothing sticky to reopen on.
 */
export const saveStickyFilters = (
  storage: Storage | null,
  dashboardId: string,
  snapshot: StickyFilterSnapshot
): void => {
  try {
    if (isSnapshotEmpty(snapshot)) {
      storage?.removeItem(storageKey(dashboardId));
    } else {
      storage?.setItem(
        storageKey(dashboardId),
        JSON.stringify({ v: STORAGE_VERSION, ...snapshot })
      );
    }
  } catch {
    // Quota exceeded / private mode: stickiness is best-effort.
  }
};
